import { useEffect, useState } from "react";
import styles from "./PointsBurst.module.css";

export interface PointsBurstProps {
  points: number;
  /** Changes identity exactly when a new burst should play (e.g. the round/duel index, or a
      "revealed" flag) — the component doesn't infer "new score" from `points` alone, since a
      score of 0 twice in a row is still two distinct events. */
  triggerKey: string | number;
}

const COUNT_MS = 700;
const VISIBLE_MS = 1400;

/**
 * Full-screen celebratory overlay: counts up to `points` with an easing
 * animation, pops in, then rises and fades out on its own — purely a
 * "that felt good" layer on top of the reveal, never blocks interaction
 * (pointer-events: none) and never the only place the score is shown (the
 * permanent reveal UI underneath still shows the final number statically).
 */
export function PointsBurst({ points, triggerKey }: PointsBurstProps) {
  const [displayed, setDisplayed] = useState(0);
  const [phase, setPhase] = useState<"in" | "out" | "done">("in");

  useEffect(() => {
    setPhase("in");
    setDisplayed(0);
    const start = performance.now();
    let frame: number;

    function tick(now: number) {
      const t = Math.min(1, (now - start) / COUNT_MS);
      const eased = 1 - (1 - t) ** 3;
      setDisplayed(Math.round(eased * points));
      if (t < 1) frame = requestAnimationFrame(tick);
    }
    frame = requestAnimationFrame(tick);

    const outTimer = window.setTimeout(() => setPhase("out"), VISIBLE_MS - 300);
    const doneTimer = window.setTimeout(() => setPhase("done"), VISIBLE_MS);

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(outTimer);
      window.clearTimeout(doneTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerKey]);

  if (phase === "done") return null;

  const negative = points < 0;

  return (
    <div className={styles.overlay} aria-hidden>
      <div
        className={`${styles.badge} ${negative ? styles.badgeNegative : ""} ${phase === "out" ? styles.badgeOut : styles.badgeIn}`}
      >
        {displayed > 0 ? "+" : ""}
        {displayed}
      </div>
    </div>
  );
}
